import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { TenantProvider, useTenant } from "./context/TenantContext";
import { CartProvider } from "./context/CartContext";
import CartOverlay from "./components/CartOverlay";
import GlobalCartButton from "./components/GlobalCartButton";
import CookieBanner from "./components/CookieBanner";
import UnifiedAdminLogin from "./pages/UnifiedAdminLogin";
import UnifiedAdminDashboard from "./features/unified/UnifiedAdminDashboard";
import { WalletLanding, WalletLogin, WalletRegister, WalletDashboard } from "./WalletPortal";
import MemberRegister from "./features/member/MemberRegister";
import AuthCallback from "./features/shop/AuthCallback";
import GoogleContinue from "./features/shop/GoogleContinue";
import ExpenseForm from "./pages/ExpenseForm";
import HelpPage from "./pages/HelpPage";
import AdminDashboard from "./features/admin/AdminDashboard";
import SystemAdminLogin from "./pages/SystemAdminLogin";
import PublicShop from "./features/shop/PublicShop";
import PublicEventDetail from "./features/shop/PublicEventDetail";
import PayComplete from "./features/shop/PayComplete";
import Checkout from "./features/shop/Checkout";
import MyTickets from "./features/shop/MyTickets";
import Footer from "./components/shared/Footer";
import "./App.css";

// Default tenant slug — used when host doesn't resolve to any tenant and no override query is present.
const DEFAULT_TENANT_SLUG = "mymaitree";

// When ticketing module is on, landing shows public shop; else the legacy Wallet landing
function TenantLanding() {
  const { hasModule } = useTenant();
  return hasModule("ticketing") ? <PublicShop /> : <WalletLanding />;
}

// Hide the footer inside admin/system-admin portals so it doesn't clutter
// the dashboard chrome; show it everywhere else (public shop, member
// register, help, wallet, etc.).
function FooterGate() {
  const { pathname } = useLocation();
  const hide = /\/admin(\/|$)|\/admin\/dashboard/.test(pathname);
  if (hide) return null;
  return <Footer />;
}

// Root redirect that respects ?tenant= query param override (used for preview/testing).
function RootRedirect() {
  const params = new URLSearchParams(window.location.search);
  const overrideSlug = params.get("tenant");
  const slug = overrideSlug || DEFAULT_TENANT_SLUG;
  return <Navigate to={`/${slug}`} replace />;
}

// Legacy URL guard — runs BEFORE Routes match, so it beats specificity rules.
// - /maitree/*     → /mymaitree/*   (slug rename)
// - .../shop/...   → .../...        (drop /shop/ segment)
function LegacyRedirect({ children }) {
  const location = useLocation();
  let path = location.pathname;

  // /maitree → /mymaitree
  const maitreeMatch = path.match(/^\/maitree(\/.*)?$/);
  if (maitreeMatch) {
    const rest = maitreeMatch[1] || "";
    return <Navigate to={`/mymaitree${rest}${location.search}${location.hash}`} replace />;
  }

  // /:slug/shop, /:slug/shop/xxx → /:slug, /:slug/xxx
  if (/\/shop(\/|$)/.test(path)) {
    const rewritten = path.replace(/\/shop(\/|$)/, "$1").replace(/\/$/, "");
    return <Navigate to={`${rewritten}${location.search}${location.hash}`} replace />;
  }

  return children;
}

function App() {
  return (
    <TenantProvider>
    <AuthProvider>
      <Router>
        <CartProvider>
        <Toaster position="top-center" richColors />
        <GlobalCartButton />
        <CartOverlay />
        <LegacyRedirect>
        <Routes>
          {/* System Admin (global) */}
          <Route path="/admin" element={<SystemAdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

          {/* Root — forward to tenant portal (host or ?tenant= override) */}
          <Route path="/" element={<RootRedirect />} />

          {/* Tenant-scoped routes */}
          <Route path="/:tenantSlug" element={<TenantLanding />} />
          <Route path="/:tenantSlug/admin" element={<UnifiedAdminLogin />} />
          <Route path="/:tenantSlug/admin/dashboard" element={<UnifiedAdminDashboard />} />
          <Route path="/:tenantSlug/login" element={<WalletLogin />} />
          <Route path="/:tenantSlug/register" element={<WalletRegister />} />
          <Route path="/:tenantSlug/dashboard" element={<WalletDashboard />} />
          <Route path="/:tenantSlug/member-register" element={<MemberRegister />} />
          <Route path="/:tenantSlug/event/:eventId" element={<PublicEventDetail />} />
          <Route path="/:tenantSlug/checkout" element={<Checkout />} />
          <Route path="/:tenantSlug/pay-complete" element={<PayComplete />} />
          <Route path="/:tenantSlug/my-tickets" element={<MyTickets />} />
          <Route path="/:tenantSlug/auth-callback" element={<AuthCallback />} />
          <Route path="/:tenantSlug/google-continue" element={<GoogleContinue />} />
          <Route path="/:tenantSlug/expense/:eventId" element={<ExpenseForm />} />
          <Route path="/:tenantSlug/help" element={<HelpPage />} />
          <Route path="/help" element={<HelpPage />} />

          {/* Fallback — send to default tenant landing */}
          <Route path="*" element={<Navigate to={`/${DEFAULT_TENANT_SLUG}`} replace />} />
        </Routes>
        </LegacyRedirect>
        <FooterGate />
        <CookieBanner />
        </CartProvider>
      </Router>
    </AuthProvider>
    </TenantProvider>
  );
}

export default App;
